/* ══════════════════════════════════════════════════════════════════════════
 * WHICH FEED POSTS THIS ELEATER HAS LIKED.
 *
 * ⚠️ THIS IS `saved.js` FOR POSTS, AND IT IS DELIBERATELY THE SAME SHAPE.
 * That file already solved this exact problem for restaurant hearts — one
 * request shared by every card, a listener set so nothing drifts, and a
 * silent failure that degrades to an empty heart. Writing a second pattern
 * here would mean two answers to "is this liked" and only one of them being
 * looked at, which is the drift this repository keeps paying for.
 *
 * ⚠️ AND THEY ARE TWO DIFFERENT TABLES, WHICH IS THE WHOLE REASON BOTH EXIST.
 * `001_schema.sql` says it in its own comment: *"Eleater hearts a RESTAURANT
 * (distinct from feed_saves, which hearts a POST)."*  Liking a photograph is
 * not saving the restaurant, and the locked viewer draws them as two separate
 * controls with two separate words underneath for exactly that reason.
 *
 * ⚠️ THE ROUTE HAS EXISTED SINCE THE DAY IT WAS WRITTEN AND NO SCREEN HAD EVER
 * CALLED IT. `POST /feeds/:id/save`, `DELETE /feeds/:id/save` and
 * `GET /feeds/saved/me` are all live and all were unreachable from the app —
 * the same shape as `POST /restaurants/:id/share`, which counted every share
 * ever made as zero.
 * ══════════════════════════════════════════════════════════════════════════ */

import { useEffect, useState } from 'react';
import { call, getToken, SIGNED_IN_EVENT } from './api.js';

/* ⚠️ MODULE LEVEL, LIKE `saved.js`. A venue page opens with a grid of posts
 * and the viewer on top of it; one request serves all of them. */
let ids = new Set();
let asked = null;
const listeners = new Set();

function publish() {
  for (const fn of listeners) fn(ids);
}

function load() {
  if (asked) return asked;
  /* ⚠️ SIGNED OUT IS NOT AN EMPTY LIST, IT IS NO LIST. The route is
   * requireAuth, so asking without a token is a guaranteed 401 for an answer
   * already known. */
  if (!getToken()) { asked = Promise.resolve(); return asked; }
  asked = call('/feeds/saved/me', { auth: true })
    .then((d) => {
      /* ⚠️ WRITTEN TO ACCEPT EITHER SHAPE, for the reason `saved.js` gives:
       * a wrapper added later must not silently empty every heart at once. */
      const rows = Array.isArray(d) ? d : ((d && d.results) || []);
      ids = new Set(rows.map((r) => r.id || r.feed_id).filter(Boolean));
      publish();
    })
    /* ⚠️ A LIKE IS NOT WORTH AN ERROR MESSAGE. A failure here draws an empty
     * heart, which is what a new Eleater sees anyway, and the post still
     * opens. */
    .catch(() => {});
  return asked;
}

/* signed in on this page (no reload) — what was asked while signed out was
   "no list"; ask again for the real one */
if (typeof window !== 'undefined') {
  window.addEventListener(SIGNED_IN_EVENT, () => { asked = null; ids = new Set(); publish(); load(); });
}

/** The post ids this Eleater has liked. Re-renders the caller when it changes. */
export function useLiked() {
  const [snapshot, setSnapshot] = useState(ids);
  useEffect(() => {
    const fn = (next) => setSnapshot(next);
    listeners.add(fn);
    load();
    return () => { listeners.delete(fn); };
  }, []);
  return snapshot;
}

/** Tell every listener at once, the moment the tap happens. */
/* the list as the server has it — the after-sign-in like waits for it */
export function likedLoaded() { return load(); }

export function markLiked(id, on) {
  if (!id) return;
  const next = new Set(ids);
  if (on) next.add(id); else next.delete(id);
  ids = next;
  publish();
}

export function isLiked(id) { return ids.has(id); }
